import type React from 'react';
import { MODAL_NAME } from 'src/constants/modals';
import ConfirmationModal from 'src/components/common/ConfirmationModal';
import { JobAction, triggerJobAction } from '../../actions';
import type { IJob } from 'src/stores/JobStore/Job';
import type { IModalStore } from 'src/stores/UiStore/ModalStore';
import { inject, observer } from 'mobx-react';
import { Text, Stack, useToast } from 'braid-design-system';
import UpdateExpiryDateForm from './UpdateExpiryDateForm';
import {
  type ActionResult,
  okResult,
  failResult,
} from 'src/components/common/ActionableFormView';
import { useTranslations } from '@vocab/react';
import translations from '../.vocab';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

interface Props {
  job: IJob;
  modalStore?: IModalStore;
}

const ExpireConfirmationModal: React.FC<Props> = ({ job, modalStore }) => {
  const { t } = useTranslations(translations);
  const { router } = useAppConfig();
  const showToast = useToast();
  const onExpire = async (): Promise<ActionResult> => {
    triggerJobAction(JobAction.EXPIRE, job, router);
    const result = await (job.expire() as Promise<boolean>);
    if (result) {
      return okResult(t('This job ad has been expired successfully'));
    }

    return failResult(
      t('Sorry, we couldn’t save your changes. Can we try again?'),
    );
  };

  const onSubmitSuccess = (message: string): void => {
    modalStore!.removeModal(MODAL_NAME.EXPIRE_CONFIRMATION);
    showToast({ message, tone: 'positive', closeLabel: t('Close') });
  };

  return (
    <ConfirmationModal
      name={MODAL_NAME.EXPIRE_CONFIRMATION}
      onConfirm={onExpire}
      modalStore={modalStore!}
      confirmButtonLabel={t('Expire')}
      confirmButtonLoadingLabel={t('Expiring')}
      confirmErrorMessage={t(
        'Sorry, we couldn’t save your changes. Can we try again?',
      )}
      title={t('Expire ad?')}
      onSubmitSuccess={onSubmitSuccess}
      form={
        job.userStore.user.isCsView ? (
          <UpdateExpiryDateForm job={job} onSubmitSuccess={onSubmitSuccess} />
        ) : (
          <></>
        )
      }
    >
      <Stack space="gutter">
        <Text>{t('This will remove it from SEEK immediately.')}</Text>
        <Text>
          {t('To re-post this ad later you will be charged for a new ad.')}
        </Text>
        <Text>{t('All other features for managing and communicating...')}</Text>
      </Stack>
    </ConfirmationModal>
  );
};

export default inject((stores: any) => ({
  modalStore: stores.store.uiStore.modalStore,
}))(observer(ExpireConfirmationModal));
